import { applyKnot, KNOT_BEHAVIOUR, type KnotType } from './knots';
import {
  knotLeftPosition,
  knotsInRow,
  type ColorIndex,
  type NormalPattern,
} from './types';

/**
 * Simulation of a normal (diagonal-knot) pattern.
 *
 * The state being simulated is the left-to-right ORDER of the strands. A knot
 * both reveals a colour and (for the two single knots) swaps its pair, so the
 * order after row r is what decides which strands meet in row r+1. Get this
 * wrong and every row after the first is fiction, which is exactly the failure
 * mode that costs a crafter an evening.
 */

export interface SimulatedKnot {
  row: number;
  /** Index of this knot within its row. */
  index: number;
  knot: KnotType;
  /** Position of the left string of the pair, before the knot. */
  leftPosition: number;
  /** Strand ids entering the knot. */
  leftStrand: number;
  rightStrand: number;
  /** Strand whose colour is visible. */
  shownStrand: number;
  /** Colour index that is visible. */
  color: ColorIndex;
}

export interface SimulatedRow {
  row: number;
  knots: SimulatedKnot[];
  /** Strand order BEFORE this row was tied. */
  orderBefore: number[];
  /** Strand order AFTER this row was tied. */
  orderAfter: number[];
}

export interface NormalSimulation {
  strandCount: number;
  rows: SimulatedRow[];
  /** Strand order before row 0 (i.e. the setup order, left to right). */
  initialOrder: number[];
  /** Strand order after the final row. */
  finalOrder: number[];
  /** Knots tied by each strand, indexed by strand id. Used for step counts. */
  knotsPerStrand: number[];
  /**
   * Thread load per strand, indexed by strand id, measured in single-knot
   * equivalents. A forward-backward knot is physically TWO complete knots tied
   * by the same string, so it eats twice the thread of a forward knot even
   * though it is one cell on the chart. Thread estimates must use this, not
   * `knotsPerStrand`, or patterns with a lot of doubled knots come out short.
   */
  hitchPairsPerStrand: number[];
  totalKnots: number;
}

export class PatternError extends Error {}

/**
 * Run the pattern. `initialOrder` defaults to strands in id order, which is
 * how designs are authored: strand id N starts at position N.
 */
export function simulateNormal(
  pattern: NormalPattern,
  initialOrder?: number[],
): NormalSimulation {
  const strandCount = pattern.strandColors.length;
  if (strandCount < 2 || strandCount % 2 !== 0) {
    throw new PatternError(
      `A normal pattern needs an even number of strings (at least 2); got ${strandCount}.`,
    );
  }

  const order = initialOrder
    ? initialOrder.slice()
    : Array.from({ length: strandCount }, (_, i) => i);

  if (order.length !== strandCount) {
    throw new PatternError(
      `initialOrder has ${order.length} entries but the pattern has ${strandCount} strings.`,
    );
  }
  assertPermutation(order, strandCount, 'initialOrder');

  const initialOrderSnapshot = order.slice();
  const knotsPerStrand = new Array<number>(strandCount).fill(0);
  const hitchPairsPerStrand = new Array<number>(strandCount).fill(0);
  const rows: SimulatedRow[] = [];
  let totalKnots = 0;

  for (let r = 0; r < pattern.rows.length; r++) {
    const rowKnots = pattern.rows[r]!;
    const expected = knotsInRow(strandCount, r);
    if (rowKnots.length !== expected) {
      throw new PatternError(
        `Row ${r + 1} has ${rowKnots.length} knots but a ${strandCount}-string pattern needs ${expected} in that row.`,
      );
    }

    const orderBefore = order.slice();
    const simKnots: SimulatedKnot[] = [];

    for (let k = 0; k < rowKnots.length; k++) {
      const knot = rowKnots[k]!;
      const p = knotLeftPosition(r, k);
      const leftStrand = order[p]!;
      const rightStrand = order[p + 1]!;

      const result = applyKnot(knot, leftStrand, rightStrand);
      order[p] = result.left;
      order[p + 1] = result.right;

      // Both strings in the pair are physically handled, but only the working
      // string is doing the wrapping. Thread consumption follows the working
      // string, so that is what we count.
      knotsPerStrand[result.shown]! += 1;
      hitchPairsPerStrand[result.shown]! += KNOT_BEHAVIOUR[knot].hitchPairs;

      simKnots.push({
        row: r,
        index: k,
        knot,
        leftPosition: p,
        leftStrand,
        rightStrand,
        shownStrand: result.shown,
        color: pattern.strandColors[result.shown]!,
      });
      totalKnots++;
    }

    // Invariant: a row may only permute the strands, never create or lose one.
    assertPermutation(order, strandCount, `order after row ${r + 1}`);

    rows.push({ row: r, knots: simKnots, orderBefore, orderAfter: order.slice() });
  }

  return {
    strandCount,
    rows,
    initialOrder: initialOrderSnapshot,
    finalOrder: order.slice(),
    knotsPerStrand,
    hitchPairsPerStrand,
    totalKnots,
  };
}

function assertPermutation(order: number[], n: number, label: string): void {
  const seen = new Uint8Array(n);
  for (const v of order) {
    if (!Number.isInteger(v) || v < 0 || v >= n) {
      throw new PatternError(`${label} contains out-of-range strand id ${v}.`);
    }
    if (seen[v]) throw new PatternError(`${label} contains duplicate strand id ${v}.`);
    seen[v] = 1;
  }
  if (order.length !== n) {
    throw new PatternError(`${label} has length ${order.length}, expected ${n}.`);
  }
}

/**
 * Flatten a simulation to the visible colour chart: `chart[row][k]` is the
 * colour index of the k-th knot of that row. This is what the preview draws.
 */
export function colorChart(sim: NormalSimulation): ColorIndex[][] {
  return sim.rows.map((row) => row.knots.map((k) => k.color));
}

/**
 * Render a simulation as compact text, one line per row, using chart glyphs.
 * Used by tests and by the "show me the raw pattern" view.
 */
export function chartToText(sim: NormalSimulation): string {
  return sim.rows
    .map((row) => {
      const pad = row.row % 2 === 0 ? '' : '  ';
      return pad + row.knots.map((k) => k.knot.padEnd(2, ' ')).join('  ').trimEnd();
    })
    .join('\n');
}

/**
 * Render the visible colours as a letter grid (A, B, C...), aligned so that a
 * row's knots sit between its two strings. Rows with the offset are indented.
 * This makes chevrons and stripes legible in a plain test assertion.
 */
export function colorsToText(sim: NormalSimulation): string {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  return sim.rows
    .map((row) => {
      const pad = row.row % 2 === 0 ? '' : '  ';
      return (pad + row.knots.map((k) => letters[k.color] ?? '?').join('   ')).trimEnd();
    })
    .join('\n');
}

/**
 * The vertical repeat: how many rows until the pattern starts over.
 *
 * This deliberately compares the COLOUR order, not the strand-id order. Two
 * strands of the same colour are interchangeable to the eye and to the crafter,
 * so a mirrored chevron visually repeats every 8 rows even though the
 * underlying strand permutation takes 16 rows to close. Reporting 16 would be
 * technically true and practically useless.
 *
 * Returns null when the pattern does not close within `maxRows`, which means
 * the crafter cannot simply loop the chart.
 */
export function findRepeatLength(
  pattern: NormalPattern,
  maxRows = 512,
): number | null {
  const strandCount = pattern.strandColors.length;
  const colorsOf = (o: number[]) => o.map((s) => pattern.strandColors[s]!);
  const start = Array.from({ length: strandCount }, (_, i) => i);
  const startColors = colorsOf(start);
  const order = start.slice();
  const rowCount = pattern.rows.length;
  if (rowCount === 0) return null;

  for (let r = 0; r < maxRows; r++) {
    const rowKnots = pattern.rows[r % rowCount]!;
    for (let k = 0; k < rowKnots.length; k++) {
      const p = knotLeftPosition(r, k);
      const res = applyKnot(rowKnots[k]!, order[p]!, order[p + 1]!);
      order[p] = res.left;
      order[p + 1] = res.right;
    }
    // Only whole even/odd row pairs can be a true repeat, because the row
    // offset alternates; stopping on an odd count would flip the geometry.
    if ((r + 1) % 2 === 0 && sameArray(colorsOf(order), startColors)) return r + 1;
  }
  return null;
}

/**
 * Order in which strands first appear as the left-edge "leader" of a diagonal.
 *
 * In a diagonal-knot bracelet the strings circulate: a string works its way to
 * the centre, gets pushed back out by its successors, reaches the edge and then
 * leads a fresh diagonal. Which string leads *when* is a consequence of the
 * knot grid, not something an author picks. Generators use this to decide how
 * to lay out the setup order so the visible colour sequence comes out in the
 * order the user actually asked for.
 */
export function leadStrandOrder(pattern: NormalPattern, maxRows = 512): number[] {
  const strandCount = pattern.strandColors.length;
  const order = Array.from({ length: strandCount }, (_, i) => i);
  const rowCount = pattern.rows.length;
  const leads: number[] = [];
  if (rowCount === 0) return leads;

  for (let r = 0; r < maxRows && leads.length < strandCount; r++) {
    const rowKnots = pattern.rows[r % rowCount]!;
    for (let k = 0; k < rowKnots.length; k++) {
      const p = knotLeftPosition(r, k);
      const left = order[p]!;
      const right = order[p + 1]!;
      const res = applyKnot(rowKnots[k]!, left, right);
      // Position 0 is the left edge: whoever shows there is starting a diagonal.
      if (p === 0 && !leads.includes(res.shown)) leads.push(res.shown);
      order[p] = res.left;
      order[p + 1] = res.right;
    }
  }
  return leads;
}

function sameArray(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
